import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const csvEscape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export default function Admin() {
  const [key, setKey] = useState("");
  const [apps, setApps] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [support, setSupport] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (e) => {
    e?.preventDefault();
    if (!key) return;
    setLoading(true);
    setError("");
    try {
      const headers = { "X-Admin-Key": key };
      const [appsRes, anRes, supRes, funnelRes] = await Promise.all([
        axios.get(`${API}/admin/applications`, { headers }),
        axios.get(`${API}/admin/analytics`, { headers }),
        axios.get(`${API}/admin/support`, { headers }),
        axios.get(`${API}/admin/funnel`, { headers }),
      ]);
      setApps(appsRes.data.applications);
      setAnalytics(anRes.data);
      setSupport(supRes.data.messages);
      setFunnel(funnelRes.data);
    } catch (err) {
      setApps(null);
      setError(
        err?.response?.status === 401
          ? "Galat admin key. Dobara try karo."
          : "Load nahi hua. Backend check karo."
      );
    } finally {
      setLoading(false);
    }
  }, [key]);

  const exportCsv = () => {
    const cols = [
      "slot_number", "name", "phone", "city", "car_model",
      "car_year", "status", "amount_inr", "payment_id", "created_at",
    ];
    const rows = apps.map((a) => cols.map((c) => csvEscape(a[c])).join(","));
    const blob = new Blob([cols.join(",") + "\n" + rows.join("\n")], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rydecast-pilot-001-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const isLoaded = apps !== null;

  useEffect(() => {
    if (!isLoaded) return;
    const t = setInterval(() => load(), 10000);
    return () => clearInterval(t);
  }, [isLoaded, load]);

  const ago = (ts) => {
    const s = Math.max(0, (Date.now() - new Date(ts).getTime()) / 1000);
    if (s < 60) return "abhi";
    if (s < 3600) return `${Math.floor(s / 60)} min pehle`;
    if (s < 86400) return `${Math.floor(s / 3600)} hr pehle`;
    return `${Math.floor(s / 86400)} din pehle`;
  };

  const paid = apps?.filter((a) => a.status === "paid") ?? [];
  const pending = apps?.filter((a) => a.status !== "paid") ?? [];

  return (
    <main className="min-h-screen bg-obsidian text-white font-body px-6 py-16">
      <div className="max-w-6xl mx-auto">
        <a href="/" className="font-display font-black text-xl tracking-tighter" data-testid="admin-logo">
          RYDECAST<span className="text-volt">.</span>{" "}
          <span className="text-xs uppercase tracking-[0.3em] text-neutral-500 font-body font-normal">
            Admin
          </span>
        </a>

        {apps === null ? (
          <motion.form
            onSubmit={load}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-16 max-w-sm"
            data-testid="admin-login-form"
          >
            <label
              className="block text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2"
              htmlFor="admin-key"
            >
              Admin key
            </label>
            <input
              id="admin-key"
              data-testid="admin-key-input"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              autoComplete="off"
              className="w-full bg-transparent border-b border-border focus:border-volt outline-none py-3.5 text-white transition-colors duration-300"
            />
            {error && (
              <p data-testid="admin-error" className="mt-4 text-sm text-red-400">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-button"
              disabled={loading}
              className="mt-8 bg-volt text-black font-bold px-8 py-3.5 hover:scale-105 transition-transform duration-300 disabled:opacity-60"
            >
              {loading ? "Loading…" : "Open Dashboard"}
            </button>
          </motion.form>
        ) : (
          <div className="mt-12" data-testid="admin-dashboard">
            {analytics && (
              <div className="mb-10" data-testid="admin-analytics">
                <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Visitors</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-border">
                  <div className="border-r border-b border-border p-5">
                    <p className="font-display font-black text-3xl text-white">{analytics.today}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-1.5">Visits today</p>
                  </div>
                  <div className="border-r border-b border-border p-5">
                    <p className="font-display font-black text-3xl text-white">{analytics.total}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-1.5">Total visits</p>
                  </div>
                  <div className="border-r border-b border-border p-5 col-span-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">Top sources</p>
                    {analytics.top_referrers.length === 0 ? (
                      <p className="text-sm text-neutral-600">Direct / no referrer yet</p>
                    ) : (
                      analytics.top_referrers.map((r) => (
                        <p key={r.referrer} className="text-xs text-neutral-400 truncate">
                          {r.referrer} <span className="text-volt">({r.count})</span>
                        </p>
                      ))
                    )}
                  </div>
                </div>
                {analytics.recent.length > 0 && (
                  <div className="border border-border border-t-0 px-5 py-4 max-h-40 overflow-y-auto">
                    {analytics.recent.map((v, i) => (
                      <p key={i} className="text-xs text-neutral-500 py-1 flex justify-between gap-4">
                        <span className="truncate">{v.path}{v.referrer ? ` ← ${v.referrer}` : ""}</span>
                        <span className="shrink-0 text-neutral-600">
                          {new Date(v.ts).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {analytics && (
              <div className="mb-10 border border-volt/40" data-testid="admin-live-panel">
                <div className="border-b border-border px-5 py-4 flex items-center gap-3">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-volt opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-volt" />
                  </span>
                  <p className="font-display font-black text-xl text-white tracking-tight">
                    {analytics.online_now}{" "}
                    <span className="text-sm font-body font-normal text-neutral-400">
                      on site right now
                    </span>
                  </p>
                  <span className="ml-auto text-[10px] uppercase tracking-[0.25em] text-volt">
                    LIVE · refreshes 10s
                  </span>
                </div>
                <div className="max-h-72 overflow-y-auto px-5 py-3">
                  {(!analytics.events || analytics.events.length === 0) && (
                    <p className="py-6 text-sm text-neutral-600 text-center">Abhi koi activity nahi.</p>
                  )}
                  {analytics.events?.map((e, i) => (
                    <div
                      key={i}
                      className="flex items-baseline gap-4 py-2.5 border-b border-border/40 last:border-b-0"
                      data-testid={`live-event-${i}`}
                    >
                      <span
                        className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 shrink-0 ${
                          e.type === "payment"
                            ? "bg-volt text-black"
                            : e.type === "application"
                              ? "bg-neutral-800 text-white"
                              : e.type === "question"
                                ? "bg-neutral-800 text-volt"
                                : "bg-neutral-900 text-neutral-400"
                        }`}
                      >
                        {e.type === "payment"
                          ? "₹ PAID"
                          : e.type === "application"
                            ? "FORM"
                            : e.type === "question"
                              ? "ASK"
                              : "VISIT"}
                      </span>
                      <span className="text-sm text-neutral-300 flex-1 truncate">{e.detail}</span>
                      <span className="text-xs text-neutral-600 shrink-0">{ago(e.ts)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {funnel && (
              <div className="mb-10 border border-border" data-testid="admin-funnel-panel">
                <div className="border-b border-border px-5 py-4 flex flex-wrap items-baseline justify-between gap-3">
                  <div>
                    <h2 className="font-display font-bold text-2xl tracking-tight">Sales funnel</h2>
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-1">
                      Visit → checkout → form → order → payment
                    </p>
                  </div>
                  <p className="text-xs uppercase tracking-[0.2em] text-volt">
                    {funnel.unique_sessions} tracked sessions
                  </p>
                </div>
                <div className="grid md:grid-cols-3 border-b border-border">
                  {funnel.milestones.map((m, i) => (
                    <div key={m.key} className="border-b md:border-b-0 md:border-r border-border last:border-r-0 p-5" data-testid={`funnel-${m.key}`}>
                      <div className="flex items-baseline justify-between gap-4">
                        <p className="font-display font-black text-3xl text-white">{m.count}</p>
                        <p className="text-[10px] uppercase tracking-[0.15em] text-volt">
                          {m.rate_from_start}% of visits
                        </p>
                      </div>
                      <p className="mt-2 text-xs uppercase tracking-[0.18em] text-neutral-500">{m.label}</p>
                      <div className="mt-4 h-1 bg-secondary overflow-hidden">
                        <div className="h-full bg-volt" style={{ width: `${Math.min(m.rate_from_start, 100)}%` }} />
                      </div>
                      {i > 0 && (
                        <p className="mt-2 text-[11px] text-neutral-600">
                          {m.rate_from_previous}% from previous step
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="grid lg:grid-cols-3">
                  <div className="p-5 border-b lg:border-b-0 lg:border-r border-border">
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-3">Top actions</p>
                    {funnel.event_counts.slice(0, 10).map((e) => (
                      <p key={e.event} className="text-xs text-neutral-400 py-1.5 flex justify-between gap-4">
                        <span>{e.event}</span>
                        <span className="text-volt">{e.count}</span>
                      </p>
                    ))}
                    {funnel.event_counts.length === 0 && (
                      <p className="text-sm text-neutral-600">Behavior events will appear as visitors interact.</p>
                    )}
                  </div>
                  <div className="p-5 border-b lg:border-b-0 lg:border-r border-border">
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-3">Top pages</p>
                    {funnel.paths.map((p) => (
                      <p key={p.path} className="text-xs text-neutral-400 py-1.5 flex justify-between gap-4">
                        <span>{p.path}</span>
                        <span className="text-volt">{p.count}</span>
                      </p>
                    ))}
                  </div>
                  <div className="p-5 max-h-64 overflow-y-auto">
                    <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-3">Recent behavior</p>
                    {funnel.recent_events.map((e, i) => (
                      <p key={`${e.ts}-${i}`} className="text-xs text-neutral-400 py-1.5 border-b border-border/40 last:border-b-0" data-testid={`behavior-event-${i}`}>
                        <span className="text-volt">{e.event}</span>
                        {e.label ? ` · ${e.label}` : ""}
                        <span className="text-neutral-600"> · {e.path} · {ago(e.ts)}</span>
                      </p>
                    ))}
                    {funnel.recent_events.length === 0 && (
                      <p className="text-sm text-neutral-600">No behavior events yet.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-border">
              {[
                ["Paid slots", paid.length],
                ["Pending (unpaid)", pending.length],
                ["Slots cap", "None — open"],
                ["Collected", `₹${(paid.reduce((s, a) => s + (a.amount_inr || 0), 0)).toLocaleString("en-IN")}`],
              ].map(([l, v]) => (
                <div key={l} className="border-r border-b border-border p-5">
                  <p className="font-display font-black text-3xl text-volt">{v}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-1.5">{l}</p>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-10 mb-4">
              <h2 className="font-display font-bold text-2xl tracking-tight">Support questions</h2>
              <span className="text-xs uppercase tracking-[0.2em] text-neutral-500">{support.length} total</span>
            </div>
            <div className="border border-border mb-12" data-testid="admin-support-list">
              {support.length === 0 && (
                <p className="px-5 py-8 text-center text-neutral-600 text-sm">Koi sawaal nahi aaya abhi tak.</p>
              )}
              {support.map((m, i) => (
                <div key={i} className="border-b border-border/60 last:border-b-0 px-5 py-4 flex flex-wrap items-baseline gap-x-6 gap-y-1" data-testid={`support-msg-${i}`}>
                  <span className="font-display font-bold text-white text-sm">{m.name}</span>
                  <a href={`tel:+91${m.phone}`} className="text-volt text-sm">+91 {m.phone}</a>
                  <span className="text-sm text-neutral-300 flex-1 min-w-48">{m.message}</span>
                  <span className="text-xs text-neutral-600 shrink-0">
                    {new Date(m.ts).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-10 mb-4">
              <h2 className="font-display font-bold text-2xl tracking-tight">Applications</h2>
              <div className="flex gap-4">
                <button
                  onClick={load}
                  data-testid="admin-refresh-button"
                  className="text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-white border border-border px-4 py-2.5 transition-colors duration-300"
                >
                  Refresh
                </button>
                <button
                  onClick={exportCsv}
                  data-testid="admin-export-button"
                  className="text-xs uppercase tracking-[0.2em] bg-volt text-black font-bold px-4 py-2.5 hover:scale-105 transition-transform duration-300"
                >
                  Export CSV
                </button>
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.post(
                        `${API}/admin/cleanup-mock`,
                        {},
                        { headers: { "X-Admin-Key": key } }
                      );
                      toast.success(`${res.data.deleted} demo booking(s) removed`);
                      await load();
                    } catch {
                      toast.error("Cleanup failed");
                    }
                  }}
                  data-testid="admin-cleanup-button"
                  className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300 border border-red-400/40 px-4 py-2.5 transition-colors duration-300"
                >
                  Clean Demo Data
                </button>
              </div>
            </div>

            <div className="overflow-x-auto border border-border">
              <table className="w-full text-sm" data-testid="admin-table">
                <thead>
                  <tr className="border-b border-border text-left">
                    {["Slot", "Name", "Phone", "City", "Car", "Status", "Amount", "Date"].map((h) => (
                      <th key={h} className="px-4 py-3.5 text-xs uppercase tracking-[0.2em] text-neutral-500 font-medium">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {apps.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-neutral-600">
                        Abhi tak koi application nahi.
                      </td>
                    </tr>
                  )}
                  {apps.map((a) => (
                    <tr
                      key={a.id}
                      className="border-b border-border/60 hover:bg-secondary/40 transition-colors duration-200"
                      data-testid={`admin-row-${a.id}`}
                    >
                      <td className="px-4 py-3.5 font-display font-bold text-volt">
                        {a.queue_id || (a.slot_number ? `#${String(a.slot_number).padStart(4, "0")}` : "—")}
                      </td>
                      <td className="px-4 py-3.5">{a.name}</td>
                      <td className="px-4 py-3.5 text-neutral-400">+91 {a.phone}</td>
                      <td className="px-4 py-3.5 text-neutral-400">{a.city}</td>
                      <td className="px-4 py-3.5 text-neutral-400">
                        {a.car_model ? `${a.car_model} (${a.car_year})` : a.plan === "full" ? "Full payment" : "Batch booking"}
                        {a.order_status ? ` · ${a.order_status}` : ""}
                      </td>
                      <td className="px-4 py-3.5">
                        <span
                          className={`text-xs uppercase tracking-[0.15em] px-2.5 py-1 ${
                            a.status === "paid"
                              ? "bg-volt/10 text-volt"
                              : "bg-neutral-800 text-neutral-400"
                          }`}
                        >
                          {a.status}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">₹{a.amount_inr}</td>
                      <td className="px-4 py-3.5 text-neutral-500">
                        {new Date(a.created_at).toLocaleDateString("en-IN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
